import { rpx } from 'client/lib/rpx-client';
import { useEffect, useState } from 'preact/hooks';
import dayjs from 'dayjs';

type Snapshot = Extract<
  Awaited<ReturnType<typeof rpx.engagementRetention.getOpBillingSnapshot>>,
  { kind: 'snapshot' }
>;
type NoMatch = Extract<
  Awaited<ReturnType<typeof rpx.engagementRetention.getOpBillingSnapshot>>,
  { kind: 'no_match' }
>;

type Result = Awaited<ReturnType<typeof rpx.engagementRetention.getOpBillingSnapshot>>;

export function OpBillingCard(props: { userId: UUID }) {
  const [result, setResult] = useState<Result | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setResult(undefined);
    setError(undefined);
    rpx.engagementRetention
      .getOpBillingSnapshot({ userId: props.userId })
      .then((r) => {
        if (!cancelled) {
          setResult(r);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [props.userId]);

  if (result?.kind === 'hidden') {
    return undefined;
  }

  let body: preact.ComponentChildren;
  if (error) {
    body = <ErrorState message={error} />;
  } else if (!result) {
    body = <Skeleton />;
  } else if (result.kind === 'error') {
    body = <ErrorState message={result.message} />;
  } else if (result.kind === 'no_match') {
    body = <NoMatchState data={result} />;
  } else {
    body = <SnapshotView data={result} />;
  }

  return (
    <section>
      <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        External billing
      </h3>
      <div class="flex flex-col gap-1">{body}</div>
    </section>
  );
}

function Skeleton() {
  return (
    <div class="flex flex-col gap-2">
      <div class="h-5 w-1/3 rounded bg-gray-100" />
      <div class="h-4 w-2/3 rounded bg-gray-100" />
      <div class="h-4 w-1/2 rounded bg-gray-100" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <p class="text-sm text-red-700">
      Couldn't load Ontraport billing: <span class="text-gray-700">{message}</span>
    </p>
  );
}

function NoMatchState({ data }: { data: NoMatch }) {
  const otherEmails = data.triedEmails.filter(
    (e) => e.toLowerCase() !== data.primaryEmail.toLowerCase(),
  );
  return (
    <div class="flex flex-col gap-2">
      <p class="text-sm text-gray-700">
        No matching contact in Ontraport for <span class="font-medium">{data.primaryEmail}</span>.
      </p>
      {otherEmails.length > 0 && (
        <p class="text-xs text-gray-500">Also tried: {otherEmails.join(', ')}</p>
      )}
      <a
        href={data.opSearchUrl}
        target="_blank"
        rel="noopener noreferrer"
        class="self-start rounded border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
      >
        Search Ontraport →
      </a>
    </div>
  );
}

function SnapshotView({ data }: { data: Snapshot }) {
  const s = data.snapshot;
  const headline = buildHeadline(s.amountCents, s.term);
  return (
    <div class="flex flex-col gap-2">
      <div class="flex flex-wrap items-baseline gap-2">
        <StatusPill status={s.status} rawCode={s.rawStatusCode} />
        {headline && <span class="text-sm font-medium text-gray-900">{headline}</span>}
      </div>

      <dl class="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm">
        {s.joinAt && (
          <Row label="Join date">
            <span class="text-gray-700">{formatOpDate(s.joinAt)}</span>
          </Row>
        )}
        {s.firstPaidAt && (
          <Row label="First paid">
            <span class="text-gray-700">{formatOpDate(s.firstPaidAt)}</span>
          </Row>
        )}
        {s.nextAutoOrderAt && (
          <Row label="Next charge">
            <span class={data.isStaleNextOrder ? 'text-amber-700' : 'text-gray-700'}>
              {formatOpDate(s.nextAutoOrderAt)}
            </span>
          </Row>
        )}
        {s.trialEndAt && (
          <Row label="Trial ends">
            <span class="text-gray-700">{formatOpDate(s.trialEndAt)}</span>
          </Row>
        )}
      </dl>

      {data.isStaleNextOrder && (
        <p class="text-xs text-amber-700">
          Next-charge date looks stale — Ontraport may not have advanced it yet. Verify in OP.
        </p>
      )}

      <MatchFootnote data={data} />

      <a
        href={data.opContactUrl}
        target="_blank"
        rel="noopener noreferrer"
        class="self-start rounded border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
      >
        View contact #{data.contactId} in Ontraport →
      </a>

      {data.otherContacts.length > 0 && (
        <p class="text-xs text-gray-500">
          Heads up: OP has {data.otherContacts.length} other{' '}
          {data.otherContacts.length === 1 ? 'contact' : 'contacts'} matching this user —{' '}
          {data.otherContacts.map((c, i) => (
            <span key={c.id}>
              {i > 0 && ', '}
              <a
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                class="text-indigo-600 hover:underline"
              >
                #{c.id}
              </a>
            </span>
          ))}
          . Worth de-duping in OP.
        </p>
      )}
    </div>
  );
}

function MatchFootnote({ data }: { data: Snapshot }) {
  if (data.matchedVia === 'current_email') {
    return undefined;
  }
  const label = data.matchedVia === 'bound' ? 'saved match' : 'previous email on file';
  return (
    <p class="text-xs italic text-gray-500">
      Matched on <span class="font-medium not-italic">{data.matchedEmail}</span> ({label})
    </p>
  );
}

function Row(props: { label: string; children: preact.ComponentChildren }) {
  return (
    <>
      <dt class="text-gray-500">{props.label}</dt>
      <dd>{props.children}</dd>
    </>
  );
}

type Status = Snapshot['snapshot']['status'];

function StatusPill({ status, rawCode }: { status: Status; rawCode?: string }) {
  const { label, classes } = statusStyle(status, rawCode);
  return (
    <span
      class={`rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}
      title={rawCode ? `OP status code ${rawCode}` : undefined}
    >
      {label}
    </span>
  );
}

function statusStyle(
  status: Status,
  rawCode: string | undefined,
): { label: string; classes: string } {
  switch (status) {
    case 'active':
      return { label: 'Active', classes: 'bg-green-50 text-green-700' };
    case 'cancelled':
      return { label: 'Cancelled', classes: 'bg-gray-100 text-gray-700' };
    case 'free_trial':
      return { label: 'Free trial', classes: 'bg-blue-50 text-blue-700' };
    case 'pending':
      return { label: 'Pending', classes: 'bg-amber-50 text-amber-800' };
    case 'unknown':
    default:
      return {
        label: rawCode ? `Unknown (${rawCode})` : 'Unknown',
        classes: 'bg-gray-100 text-gray-500',
      };
  }
}

function buildHeadline(
  cents: number | undefined,
  term: 'monthly' | 'yearly' | undefined,
): string | undefined {
  const amount = formatAmount(cents);
  if (amount && term) {
    return `${amount}/${term === 'monthly' ? 'month' : 'year'}`;
  }
  if (amount) {
    return amount;
  }
  if (term) {
    return term === 'monthly' ? 'Monthly' : 'Yearly';
  }
  return;
}

function formatAmount(cents: number | undefined): string | undefined {
  if (!cents) {
    return;
  }
  const dollars = cents / 100;
  return `$${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`;
}

function formatOpDate(d: Date | string | undefined): string {
  if (!d) {
    return '—';
  }
  return dayjs.utc(d).format('MMM D, YYYY');
}
